import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {Controller, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";

import FormPopup from "../../../components/common/popup/form-popup";
import InputSelect from "../../../components/common/form/input-select";
import InputDate from "../../../components/common/form/input-date";
import InputSubmit from "../../../components/common/form/input-submit";
import InputUserSelect from "../../../components/common/form/input-user-select";
import ExchangeRateBtn from "../../../components/common/form/exchage-rate-btn";
import {Warning} from "../../../components/common/alert/banner";
import {fetchCurrencies, fetchTermTypes} from "../../../reducers/configReducer";
import {fetchConnections} from "../../../reducers/connectionReducer";
import {getPathParam, getSelectParam, getSelectParams} from "../../../utils/converter";
import {createDocument} from "../services/document-service";
import {getElement} from "../../../api/config";
import {EXCHANGE} from "../../../api/endpoints";

const sanitizePayload = (data) => Object.entries(data ?? {}).reduce((accumulator, [key, value]) => {
    if (value === null || value === undefined || value === "") {
        return accumulator;
    }

    if (typeof value === "object" && !Array.isArray(value)) {
        const cleaned = sanitizePayload(value);
        if (Object.keys(cleaned).length > 0) {
            accumulator[key] = cleaned;
        }
        return accumulator;
    }

    accumulator[key] = value;
    return accumulator;
}, {});

const buildPayload = (values, shouldIncludeExchange) => {
    const payload = {
        connection: values.connection?.value
            ? {id: values.connection.value}
            : null,
        expire_date: values.expire_date || null,
        terms_type: values.terms_type?.value
            ? {id: values.terms_type.value}
            : null,
        delivery_date: values.delivery_date || null,
        currency: values.currency?.value
            ? {id: values.currency.value}
            : null,
    };

    if (shouldIncludeExchange) {
        payload.exchange_date = values.exchange_date || null;
        const numericRate = typeof values.exchange_rate === "string"
            ? Number(values.exchange_rate)
            : values.exchange_rate;
        payload.exchange_rate = Number.isFinite(numericRate) ? numericRate : null;
    }

    return sanitizePayload(payload);
};

const DEFAULT_FORM_VALUES = {
    connection: null,
    expire_date: "",
    terms_type: null,
    delivery_date: "",
    currency: null,
    exchange_date: "",
    exchange_rate: "",
};

const DocumentCreatePopup = ({
    isOpen,
    onClose,
    type,
    onSuccess,
    initialValues,
    selectedUsersDefault,
    submitAction,
    modalTitle,
    submitLabel,
}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {loggedCompany} = useSelector((state) => state.user);
    const {company} = useSelector((state) => state.userCompany);
    const {
        currenciesList,
        isCurrenciesListLoading,
        termTypesList,
        isTermTypesListLoading,
    } = useSelector((state) => state.config);
    const {
        connections,
        isLoadingConnections,
    } = useSelector((state) => state.connection);

    const companyCurrencyCode = company?.currency?.code ?? null;
    const companyCurrencySymbol = company?.currency?.currency_symbol ?? "";

    const connectionOptions = useMemo(
        () => getSelectParams(connections, "business_name"),
        [connections]
    );

    const {
        control,
        handleSubmit,
        watch,
        reset,
        setValue,
        getValues,
    } = useForm({
        defaultValues: {...DEFAULT_FORM_VALUES},
    });

    const resolvedInitialValues = useMemo(() => {
        const base = {...DEFAULT_FORM_VALUES};
        if (initialValues && typeof initialValues === "object") {
            Object.entries(initialValues).forEach(([key, value]) => {
                if (value !== undefined) {
                    base[key] = value;
                }
            });
        }
        return base;
    }, [initialValues]);

    const resolvedSelectedUsers = useMemo(
        () => (Array.isArray(selectedUsersDefault) ? selectedUsersDefault : []),
        [selectedUsersDefault]
    );

    const watchedCurrency = watch("currency");
    const companyCurrencyCodeUpper = typeof companyCurrencyCode === "string" ? companyCurrencyCode.toUpperCase() : null;
    const watchedCurrencyCodeUpper = typeof watchedCurrency?.code === "string" ? watchedCurrency.code.toUpperCase() : null;

    const shouldShowExchangeFields = Boolean(
        watchedCurrencyCodeUpper
        && companyCurrencyCodeUpper
        && watchedCurrencyCodeUpper !== companyCurrencyCodeUpper
    );

    useEffect(() => {
        if (shouldShowExchangeFields) {
            return;
        }

        const currentExchangeDate = getValues("exchange_date");
        const currentExchangeRate = getValues("exchange_rate");

        if (currentExchangeDate) {
            setValue("exchange_date", "", {shouldDirty: true});
        }

        const hasExchangeRateValue = currentExchangeRate !== "" && currentExchangeRate !== null && currentExchangeRate !== undefined;
        if (hasExchangeRateValue) {
            setValue("exchange_rate", "", {shouldDirty: true});
        }
    }, [getValues, setValue, shouldShowExchangeFields]);

    const [selectedConnection, setSelectedConnection] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isSubmitting, setSubmitting] = useState(false);
    const [isExchangeLoading, setExchangeLoading] = useState(false);
    const [exception, setException] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        dispatch(fetchCurrencies());
        dispatch(fetchTermTypes());
    }, [dispatch, isOpen]);

    useEffect(() => {
        if (!isOpen || !loggedCompany?.id) {
            return;
        }

        dispatch(fetchConnections({
            company_id: loggedCompany.id,
            params: {find_all: true},
        }));
    }, [dispatch, isOpen, loggedCompany]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        reset(resolvedInitialValues);
        const connectionValue = resolvedInitialValues.connection;
        if (connectionValue && connectionValue.value) {
            setSelectedConnection(connectionValue);
        } else {
            setSelectedConnection(null);
        }
        setSelectedUsers(resolvedSelectedUsers);
        setException(null);
    }, [isOpen, reset, resolvedInitialValues, resolvedSelectedUsers]);

    useEffect(() => {
        const currentCurrency = getValues("currency");
        if (!selectedConnection || !selectedConnection.value) {
            if (currentCurrency) {
                setValue("currency", null, {shouldDirty: true});
            }
            return;
        }

        const resolved = connections.find((item) => item?.id === selectedConnection.value);
        if (!resolved) {
            return;
        }

        const resolvedCurrency = resolved.importer_currency
            ? getSelectParam(resolved.importer_currency, "code")
            : null;

        if (!resolvedCurrency || Object.keys(resolvedCurrency).length === 0) {
            if (currentCurrency) {
                setValue("currency", null, {shouldDirty: true});
            }
            return;
        }

        if (!currentCurrency || currentCurrency.value !== resolvedCurrency.value) {
            setValue("currency", resolvedCurrency, {shouldDirty: true});
        }
    }, [connections, getValues, selectedConnection, setValue]);

    const handleFetchExchangeRate = useCallback(async () => {
        const currencyValue = watch("currency");
        const targetCurrencyCode = typeof currencyValue?.code === "string"
            ? currencyValue.code.toUpperCase()
            : null;
        if (!targetCurrencyCode || !companyCurrencyCodeUpper) {
            return;
        }

        setExchangeLoading(true);
        try {
            const response = await getElement(
                getPathParam([EXCHANGE, targetCurrencyCode, companyCurrencyCodeUpper])
            );
            if (response?.rate !== undefined && response?.rate !== null) {
                setValue("exchange_rate", response.rate, {shouldDirty: true});
            }
        } catch (error) {
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setExchangeLoading(false);
        }
    }, [companyCurrencyCodeUpper, setValue, t, watch]);

    const defaultSubmitAction = useCallback(
        ({companyId: submitCompanyId, type: submitType, data}) => createDocument({
            companyId: submitCompanyId,
            type: submitType,
            data,
        }),
        [],
    );

    const resolvedSubmitAction = submitAction ?? defaultSubmitAction;

    const onSubmit = useCallback(async (values) => {
        if (!loggedCompany?.id || !type) {
            toast.error(t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
            return;
        }

        setSubmitting(true);
        setException(null);
        try {
            const payload = buildPayload(values, shouldShowExchangeFields);
            let users = [];
            if (selectedUsers.length > 0) {
                // eslint-disable-next-line array-callback-return
                selectedUsers.map((user) => {
                    users.push({user: {id: user.id}});
                });

                payload.document_users = users;
            }
            const response = await resolvedSubmitAction({
                companyId: loggedCompany.id,
                type,
                data: payload,
            });
            if (typeof onSuccess === "function") {
                onSuccess(response);
            }
        } catch (error) {
            setException(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
            toast.error(error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
        } finally {
            setSubmitting(false);
        }
    }, [
        loggedCompany,
        onSuccess,
        resolvedSubmitAction,
        selectedUsers,
        shouldShowExchangeFields,
        t,
        type,
    ]);

    const popupTitle = modalTitle ?? t("app.document.new_document", {defaultValue: "New document"});
    const submitButtonLabel = submitLabel ?? t("app.common.create", {defaultValue: "Crea"});

    return (
        <FormPopup
            title={popupTitle}
            isOpen={isOpen}
            setIsOpen={(open) => {
                if (!open && !isSubmitting) {
                    onClose();
                }
            }}
            isActiveCloseBack={!isSubmitting}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Controller
                        name="connection"
                        rules={{required: true}}
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <InputSelect
                                label={t("app.orders.company", {defaultValue: "Cliente"})}
                                name={name}
                                onChange={(option) => {
                                    setSelectedConnection(option ?? null);
                                    onChange(option);
                                }}
                                options={connectionOptions}
                                value={value}
                                isLoading={isLoadingConnections}
                                isDisabled={isLoadingConnections}
                                errors={error}
                                isRequired
                            />
                        )}
                    />

                    <Controller
                        name="expire_date"
                        rules={{required: true}}
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <InputDate
                                label={t("app.documents.expire_date", {defaultValue: "Data scadenza"})}
                                name={name}
                                startDate={value}
                                onChange={onChange}
                                errors={error}
                                isShowTime={false}
                                isRequired
                            />
                        )}
                    />

                    <Controller
                        name="terms_type"
                        rules={{required: true}}
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <InputSelect
                                label={t("document.form.incoterms", {defaultValue: "Incoterms"})}
                                name={name}
                                onChange={onChange}
                                options={termTypesList}
                                value={value}
                                isLoading={isTermTypesListLoading}
                                isDisabled={isTermTypesListLoading}
                                errors={error}
                                isRequired
                            />
                        )}
                    />

                    <Controller
                        name="delivery_date"
                        rules={{required: true}}
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <InputDate
                                label={t("app.documents.delivery_date", {defaultValue: "Data consegna"})}
                                name={name}
                                startDate={value}
                                onChange={onChange}
                                errors={error}
                                isShowTime={false}
                                isRequired
                            />
                        )}
                    />


                    <div className="col-span-2">
                        <InputUserSelect
                            label={t("app.users.users")}
                            placeholder=""
                            multiple
                            excludeId={""}
                            includeAll={true}
                            selectedUsers={selectedUsers}
                            onSelect={setSelectedUsers}
                        />
                    </div>

                    <Controller
                        name="currency"
                        rules={{required: true}}
                        control={control}
                        render={({field: {onChange, value, name}, fieldState: {error}}) => (
                            <InputSelect
                                label={t("app.quotations.currency", {defaultValue: "Valuta"})}
                                name={name}
                                onChange={onChange}
                                options={currenciesList}
                                value={value}
                                isLoading={isCurrenciesListLoading}
                                isDisabled={isCurrenciesListLoading}
                                errors={error}
                                isRequired
                            />
                        )}
                    />


                    {shouldShowExchangeFields && (
                        <Controller
                            name="exchange_date"
                            rules={{required: true}}
                            control={control}
                            render={({field: {onChange, value, name}, fieldState: {error}}) => (
                                <InputDate
                                    label={t("document.form.exchange_date", {defaultValue: "Data tasso di cambio"})}
                                    name={name}
                                    startDate={value}
                                    onChange={onChange}
                                    errors={error}
                                    isShowTime={false}
                                    isRequired
                                />
                            )}
                        />
                    )}

                    {shouldShowExchangeFields && (
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium mb-1 text-gray-700">
                                {t("app.quotations.change_rate_title", {
                                    currency_1: `1 ${watchedCurrencyCodeUpper ?? watchedCurrency.code}`,
                                    currency_2: `1 ${companyCurrencyCodeUpper ?? companyCurrencyCode}`,
                                })}
                            </label>
                            <Controller
                                name="exchange_rate"
                                rules={{required: shouldShowExchangeFields}}
                                control={control}
                                render={({field: {onChange, value, name}, fieldState: {error}}) => (
                                    <ExchangeRateBtn
                                        label={t("app.quotations.change_rate_label", {defaultValue: "Tasso di cambio"})}
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        symbol={` ${companyCurrencySymbol}`}
                                        error={error}
                                        isRequired={shouldShowExchangeFields}
                                        isLoading={isExchangeLoading}
                                        onClick={handleFetchExchangeRate}
                                    />
                                )}
                            />
                        </div>
                    )}
                </div>

                {exception && (
                    <Warning message={exception} />
                )}

                <div className="flex items-center justify-end">
                    <InputSubmit
                        label={submitButtonLabel}
                        isLoading={isSubmitting}
                    />
                </div>
            </form>
        </FormPopup>
    );
};

DocumentCreatePopup.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired,
    onSuccess: PropTypes.func,
    initialValues: PropTypes.shape({
        connection: PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
        }),
        expire_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        terms_type: PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
        }),
        delivery_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        currency: PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            code: PropTypes.string,
        }),
        exchange_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        exchange_rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    selectedUsersDefault: PropTypes.arrayOf(PropTypes.shape({})),
    submitAction: PropTypes.func,
    modalTitle: PropTypes.string,
    submitLabel: PropTypes.string,
};

DocumentCreatePopup.defaultProps = {
    onSuccess: null,
    initialValues: null,
    selectedUsersDefault: [],
    submitAction: null,
    modalTitle: null,
    submitLabel: null,
};

export default DocumentCreatePopup;
